import React from 'react';
import memoizeOne from 'memoize-one';
import { graphql } from 'react-relay';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { TemplateIcon } from '@confluence/template-icon/entry-points/TemplateIcon.ts';
import { requireStyleClassResourcesForTemplates } from '@confluence/template-utils/entry-points/requireStyleClassResourcesForTemplates.ts';
import { getTemplateId } from '@confluence/template-utils/entry-points/templateUtils.ts';

import type { ConfluencePageContext, Option } from '../extensionTypes.ts';

import {
	type templateResolverQuery$data,
	type templateResolverQuery$variables,
} from './__generated__/templateResolverQuery.graphql.ts';
enum SortOrder {
	RELEVANCY_SORT = 'web.item.sorting.scheme.relevancy',
}

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- file name contains hyphens, incompatible with GraphQL naming
const templateResolverQuery = graphql`
	query templateResolverQuery($spaceKey: String!, $sortingScheme: String!) {
		allTemplates(spaceKey: $spaceKey, sortingScheme: $sortingScheme) {
			nodes {
				name
				description
				templateId
				contentBlueprintId
				iconURL
				darkModeIconURL
			}
		}
	}
`;

const loadLegacyTemplateStyles = memoizeOne(requireStyleClassResourcesForTemplates);

const getAllTemplates = async (spaceKey: string): Promise<any> => {
	const allTemplatesData = await queryInterop<
		templateResolverQuery$data,
		templateResolverQuery$variables
	>({
		query: templateResolverQuery,
		variables: { spaceKey, sortingScheme: SortOrder.RELEVANCY_SORT },
	});

	return allTemplatesData?.data.allTemplates?.nodes;
};

const search = async (spaceKey: string): Promise<Option[]> => {
	try {
		const templates = await getAllTemplates(spaceKey);

		return templates.map((template) => ({
			label: template.name,
			description: template.description,
			icon: <TemplateIcon size="small" template={template} />,
			value: getTemplateId(template),
		}));
	} catch (error) {
		getMonitoringClient().submitError(
			new Error(
				`Failed to fetch templates from template resolver in macro config panel with error: ${error}`,
			),
			{
				attribution: Attribution.PAGE_MODES,
			},
		);

		return [];
	}
};

export const templateResolverFor = (spaceKey?: string) => async (_searchTerm, _selectedValue) => {
	return spaceKey ? search(spaceKey) : [];
};

export const getTemplateResolver = ({ spaceKey }: ConfluencePageContext) => {
	loadLegacyTemplateStyles();
	return templateResolverFor(spaceKey);
};
